import { useEffect, useMemo } from 'react'
import { useDocumentsStore } from '@/stores/documents-store'
import { useReconciliationStore } from '@/stores/reconciliation-store'
import { DocumentList } from '@/components/DocumentList'
import { useBidDocumentUpload } from '@/hooks/useBidDocumentUpload'
import { BidDocumentsToolbar } from './BidDocumentsToolbar'
import { BidDocumentsDropOverlay } from './BidDocumentsDropOverlay'
import { BidDocumentsSelectionBar } from './BidDocumentsSelectionBar'

interface BidDocumentsTabProps {
  bidId: string
  readOnly?: boolean
}

/**
 * Documents tab inside the Bid Details page.
 *
 * Composes:
 *  - BidDocumentsToolbar: search / category filter / Add menu (Upload, SharePoint)
 *  - DocumentList (selectable, grouped): rows grouped by category, with
 *    checkboxes + sortable headers. Addenda are one of the groups, so the
 *    category filter and search apply to them like any other row.
 *  - BidDocumentsDropOverlay: full-panel drag overlay (hidden at rest)
 *  - BidDocumentsSelectionBar: floating bulk-action snackbar
 */
export function BidDocumentsTab({ bidId, readOnly = false }: BidDocumentsTabProps) {
  const uploadingFiles = useDocumentsStore((s) => s.uploadingFiles)
  const documentsLoading = useDocumentsStore((s) => s.isLoading)
  const documents = useDocumentsStore((s) => s.documents)
  const getVisibleDocuments = useDocumentsStore((s) => s.getVisibleDocuments)
  const searchQuery = useDocumentsStore((s) => s.searchQuery)
  const categoryFilter = useDocumentsStore((s) => s.categoryFilter)
  const sortKey = useDocumentsStore((s) => s.sortKey)
  const sortDir = useDocumentsStore((s) => s.sortDir)

  const loadReconciliationRuns = useReconciliationStore((s) => s.loadRuns)

  const { openFilePicker, openFolderPicker, handleFiles, uploadDialog } = useBidDocumentUpload({
    bidId,
    disabled: readOnly,
  })

  // Load reconciliation runs so addendum rows can show their run status.
  useEffect(() => {
    void Promise.resolve(loadReconciliationRuns(bidId)).catch(() => {
      // Non-fatal — addendum rows just fall back to the "Review changes" CTA.
    })
  }, [bidId, loadReconciliationRuns])

  // Recompute when any of the inputs change. getVisibleDocuments closes over
  // current store state, so we list its dependencies explicitly.
  const visibleDocuments = useMemo(
    () => getVisibleDocuments(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [documents, searchQuery, categoryFilter, sortKey, sortDir, getVisibleDocuments]
  )

  return (
    <div className="flex flex-col h-full gap-3">
      <BidDocumentsToolbar
        bidId={bidId}
        documentCount={documents.length}
        onOpenFilePicker={openFilePicker}
        onOpenFolderPicker={openFolderPicker}
        readOnly={readOnly}
      />

      {/* Fills the remaining height so the floating selection bar (absolute
          bottom-4) pins to the bottom of the viewport rather than to the
          bottom of a short list. min-h-0 lets the inner list scroll. */}
      <div className="relative flex-1 min-h-0">
        <div className="bg-card rounded-lg border overflow-auto h-full">
          <DocumentList
            documents={visibleDocuments}
            uploadingFiles={uploadingFiles}
            isLoading={documentsLoading}
            readOnly={readOnly}
            selectable={!readOnly}
            grouped
            bidId={bidId}
          />
        </div>

        <BidDocumentsDropOverlay
          onFilesDropped={handleFiles}
          disabled={readOnly}
        />

        <BidDocumentsSelectionBar readOnly={readOnly} />
      </div>

      {uploadDialog}
    </div>
  )
}
